import classnames from 'classnames';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { forceCheck } from 'react-lazyload';

import { Button } from 'src/app/components/Button/Button';
import { InfiniteScrollListener } from 'src/app/components/Infinite/InfiniteScrollListener';
import { Loader } from 'src/app/components/Loader/Loader';
import { withInjectedStyles } from 'src/app/helpers/styles';
import { InjectedStylesProps } from 'src/app/models/styles';

type MyInjectedProps = InjectedStylesProps;

interface MyOwnProps {
  buttonMessageId?: string;
  buttonMessageValues?: {};
  className?: string;
  classNameButton?: string;
  classNameButtonWrapper?: string;
  hasMore?: boolean;
  id?: string;
  loadMore?: VoidFunction;
  scrollLoading?: number;
}

type MyProps = MyOwnProps & MyInjectedProps;

interface MyState {
  isLoading: boolean;
  iteration: number;
}

@withInjectedStyles({
  base: [],
  desktop: [require('./InfiniteLoadMore.desktop.scss')]
})
class MyClassInfiniteLoadMore extends React.Component<MyProps, MyState> {
  private childrenContainer?: HTMLDivElement;
  private prevHeight = 0;

  constructor(props: MyProps) {
    super(props);

    this.state = {
      isLoading: false,
      iteration: 0
    };

    this.triggerButtonClick = this.triggerButtonClick.bind(this);
    this.loadMore = this.loadMore.bind(this);
    this.setChildrenContainer = this.setChildrenContainer.bind(this);
  }

  componentDidUpdate(prevProps: MyProps): void {
    this.handleDeletedChild(prevProps);

    if (this.childrenContainer && this.state.isLoading && (this.prevHeight < this.childrenContainer.scrollHeight || !this.props.hasMore)) {
      this.prevHeight = this.childrenContainer.scrollHeight;
      this.setState({
        isLoading: false,
        iteration: this.state.iteration + 1
      });
    } else if (this.childrenContainer && !this.state.isLoading && (this.prevHeight > this.childrenContainer.scrollHeight)) {
      this.prevHeight = this.childrenContainer.scrollHeight;
    }
  }

  render(): JSX.Element {
    const { children, className, hasMore, id, isMobile, scrollLoading } = this.props;
    const { isLoading, iteration } = this.state;
    const isShowingButton = (isMobile || !scrollLoading || iteration >= scrollLoading) && scrollLoading !== undefined;

    return (
      <>
        <div className={className} id={id} ref={this.setChildrenContainer}>
          {children}
        </div>

        {isLoading && <Loader />}
        {hasMore && !isLoading && isShowingButton && this.buttonLoadMore}
        {hasMore && !isLoading && !isShowingButton && this.childrenContainer && <InfiniteScrollListener onLoadMore={this.loadMore} scrollComponent={this.childrenContainer} />}
      </>
    );
  }

  private get buttonLoadMore(): JSX.Element {
    const { buttonMessageId, buttonMessageValues, classNameButton, classNameButtonWrapper } = this.props;
    const messageId = buttonMessageId || 'button.load.more';

    const button = (
      <Button className={classnames('d-block w-100 mt-16 mt@md-60 button@md--format-42 flex-1 tt-uppercase fs-14 ls-2 b-1 sg-white fg-white infinite-load-more__button', classNameButton)} modifier="black" onClick={this.triggerButtonClick}>
        <FormattedMessage id={messageId} values={buttonMessageValues} />
      </Button>
    );
    return classNameButtonWrapper ? <div className={classNameButtonWrapper}>{button}</div> : button;
  }

  private setChildrenContainer(ref: HTMLDivElement): void {
    this.childrenContainer = ref;
    this.prevHeight = ref && ref.scrollHeight;
    this.forceUpdate();
  }

  private handleDeletedChild(prevProps: MyProps & { children?: React.ReactNode; }): void {
    if (this.props.children instanceof Array && prevProps.children instanceof Array &&
        this.props.children.length < prevProps.children.length) {
      forceCheck();
    }
  }

  private triggerButtonClick(): void {
    this.setState({ iteration: 0 });
    this.loadMore();
  }

  private loadMore(): void {
    if (this.props.loadMore) {
      this.setState({ isLoading: true });
      this.props.loadMore();
    }
  }
}

export const InfiniteLoadMore = MyClassInfiniteLoadMore as React.ComponentClass<MyOwnProps>;
